import React, { RefObject } from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import {
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
  socialNavigation,
} from '@shared/constants/navigate';
import { Container, ContentContainer } from '@components/atoms';
import { Navigate, Social } from '@components/molecules';
import Logo from '@public/icons/logo_full.svg';
import { device } from '@styles/mediaQuery';
import useDevice from '@hooks/useDevice';
import { EMAIL, PHONE_NUMBER } from '@shared/constants/base';
import { useSectionNavigation } from '@hooks/useSectionNavigation';

type FooterProps = {
  refs: RefObject<HTMLDivElement>[];
};

export default function Footer({ refs }: FooterProps) {
  const { isMobile, isTablet } = useDevice();
  const { onLinkClick } = useSectionNavigation(refs);

  return (
    <StyledFooter>
      <Container>
        <Content>
          <ContactInfo>
            <Wrapper $height="40px" $alignItems="center">
              <Logo />
            </Wrapper>
            <Wrapper $height="30px" $alignItems="center">
              <StyledP>{PHONE_NUMBER.layer}</StyledP>
            </Wrapper>
            <Wrapper $height="30px" $alignItems="center">
              <StyledA href={`mailto:${EMAIL}`}>{EMAIL}</StyledA>
            </Wrapper>
          </ContactInfo>

          <MenuAndTerms>
            <Wrapper $height="40px" $alignItems="center">
              <Social
                ariaLabel="Contact and social links"
                links={socialNavigation}
              />
            </Wrapper>

            <Wrapper $height="30px" $alignItems="center">
              <Navigate ariaLabel="Site navigation" onLinkClick={onLinkClick} />
            </Wrapper>

            <Wrapper
              $height={isMobile || isTablet ? 'auto' : '30px'}
              $alignItems="center"
            >
              <Copyright>
                <CopyrightText>
                  © 2026 All Rights Reserved. Greenline Logistics.
                </CopyrightText>

                <LegalLink href={TERMS_AND_CONDITIONS}>
                  Terms and Conditions
                </LegalLink>
                <LegalLink href={PRIVACY_POLICY}>Privacy Policy</LegalLink>
              </Copyright>
            </Wrapper>
          </MenuAndTerms>
        </Content>
      </Container>
    </StyledFooter>
  );
}

const StyledFooter = styled.footer`
  width: 100%;

  ${device.laptopS(css`
    height: 178px;
    background-color: ${({ theme }) => theme.colors.athensGray};
  `)}
`;

const Content = styled(ContentContainer)`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;

  ${device.laptopS(css`
    flex-direction: row;
  `)}
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  ${device.laptopS(css`
    padding-left: 10px;
    min-width: 230px;
    justify-content: flex-start;
    align-items: flex-start;
  `)}
`;

const StyledP = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StyledA = styled.a`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const MenuAndTerms = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  width: 100%;

  ${device.laptopS(css`
    height: 100px;
    align-items: flex-end;
  `)}
`;

const Copyright = styled.div`
  display: flex;

  align-items: center;
  flex-direction: column;
  justify-content: center;
  flex-wrap: nowrap;

  ${device.laptopS(css`
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;

    padding-right: 10px;
  `)}
`;

const CopyrightText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.copyrightText};
  margin: 7px 0;

  ${device.laptopS(css`
    font-size: 16px;
    margin-right: 40px;
    text-wrap: nowrap;
    white-space: nowrap;

    &:last-child {
      margin-right: 0;
    }
  `)}
`;

const LegalLink = styled(Link)`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.copyrightText};
  margin: 7px 0;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.forestGreen};
    text-decoration: underline;
  }

  ${device.laptopS(css`
    font-size: 16px;
    margin-right: 40px;
    white-space: nowrap;

    &:last-child {
      margin-right: 0;
    }
  `)}
`;

const Wrapper = styled.div<{
  $height: string;
  $justifyContent?: 'center' | 'space-between' | 'flex-end' | 'flex-start';
  $alignItems?: 'center' | 'flex-end' | 'flex-start';
}>`
  display: flex;
  height: ${({ $height }) => $height};
  justify-content: center;
  align-items: ${({ $alignItems }) => $alignItems};

  ${device.laptopS(css<{
    $height?: string;
    $justifyContent?: 'center' | 'space-between' | 'flex-end' | 'flex-start';
  }>`
    height: ${({ $height }) => $height};
    justify-content: ${({ $justifyContent }) => $justifyContent};
  `)}
`;
